/**
 * Locations — percentage-based location tracking for an EPUB.
 *
 * Generates a list of CFI strings at regular character intervals,
 * enabling percentage ↔ CFI conversions for progress tracking.
 */

import EpubCFI from "./cfi/cfi";
import type { Section } from "./parser/section";

/**
 * Binary search for the insertion position of `item` in a sorted array.
 */
function locationOf(
  item: string,
  array: string[],
  compareFn: (a: string, b: string) => -1 | 0 | 1,
  start = 0,
  end = array.length,
): number {
  const pivot = (start + ((end - start) >> 1)) | 0;

  if (end - start <= 0) return pivot;

  const compared = compareFn(array[pivot], item);
  if (end - start === 1) return compared >= 0 ? pivot : pivot + 1;
  if (compared === 0) return pivot;
  if (compared === -1) return locationOf(item, array, compareFn, pivot, end);
  return locationOf(item, array, compareFn, start, pivot);
}

/**
 * Walk all text nodes under `root`.
 */
function* textNodes(root: Node): Generator<Text> {
  const doc = root.ownerDocument ?? document;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let node: Text | null;
  while ((node = walker.nextNode() as Text | null)) {
    yield node;
  }
}

export class Locations {
  private _locations: string[] = [];
  private _total = 0;
  private _break = 150;
  private _cfi = new EpubCFI();
  private _current = 0;
  private _generation = 0;

  private _spine: { each(fn: (section: Section) => void): void } | null = null;
  private _request:
    | ((url: string) => Promise<Document | string | unknown>)
    | null = null;

  /**
   * Bind the spine and request function (called by ParsedBook after construction).
   */
  setup(
    spine: { each(fn: (section: Section) => void): void },
    request: (url: string) => Promise<Document | string | unknown>,
  ): void {
    this._spine = spine;
    this._request = request;
  }

  /**
   * Generate locations by walking every spine section.
   * @param chars - Character interval (default 150)
   */
  async generate(chars?: number): Promise<string[]> {
    if (chars) this._break = chars;
    if (!this._spine || !this._request) {
      throw new Error(
        "Locations: spine and request not set. Call setup() first.",
      );
    }

    const sections: Section[] = [];
    this._spine.each((section) => {
      if (section.linear) sections.push(section);
    });

    const generation = ++this._generation;
    const request = this._request;
    const locations: string[] = [];
    const startedAt = performance.now();
    let sliceStartedAt = startedAt;
    for (const [index, section] of sections.entries()) {
      // Give rendering/input a turn even when archive reads resolve immediately.
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      if (generation !== this._generation) return [];
      try {
        // Use a separate document: loading/unloading the rendition's Section here
        // races page turns and can invalidate the currently displayed content.
        const doc = (await request(section.url)) as Document;
        if (generation !== this._generation) return [];
        for (const cfi of this.parse(doc.documentElement, section.cfiBase)) {
          locations.push(cfi);
          if (performance.now() - sliceStartedAt >= 8) {
            await new Promise<void>((resolve) => setTimeout(resolve, 0));
            if (generation !== this._generation) return [];
            sliceStartedAt = performance.now();
          }
        }
      } catch (error) {
        if (generation !== this._generation) return [];
        console.warn("[epub-locations] section indexing failed", {
          href: section.href,
          sectionIndex: index,
          sectionCount: sections.length,
          elapsedMs: Math.round(performance.now() - startedAt),
          error,
        });
        throw error;
      }
      if ((index + 1) % 25 === 0) {
        console.debug("[epub-locations] indexing progress", {
          sectionsCompleted: index + 1,
          sectionCount: sections.length,
          locationCount: locations.length,
          elapsedMs: Math.round(performance.now() - startedAt),
        });
      }
    }

    this._locations = locations;
    this._total = Math.max(0, locations.length - 1);
    return this._locations;
  }

  /**
   * Parse a section's content element for CFI-locations at character boundaries.
   */
  private *parse(contents: Element, cfiBase: string): Generator<string> {
    const doc = contents.ownerDocument!;
    const body = doc.body ?? doc.querySelector("body") ?? contents;
    const _break = this._break;
    let counter = 0;
    let range: {
      startContainer?: Node;
      startOffset?: number;
      endContainer?: Node;
      endOffset?: number;
    } = {};
    let prev: Text | undefined;

    for (const node of textNodes(body)) {
      const len = node.length;
      if (!node.textContent || node.textContent.trim().length === 0) continue;

      if (counter === 0) {
        range = { startContainer: node, startOffset: 0 };
      }

      let dist = _break - counter;
      let pos = 0;

      if (dist > len) {
        counter += len;
        pos = len;
      }

      while (pos < len) {
        dist = _break - counter;

        if (counter === 0) {
          range = { startContainer: node, startOffset: pos };
        }

        if (pos + dist >= len) {
          counter += len - pos;
          pos = len;
        } else {
          pos += dist;
          range.endContainer = node;
          range.endOffset = pos;

          // Build a real DOM Range for CFI generation
          const domRange = doc.createRange();
          domRange.setStart(range.startContainer!, range.startOffset!);
          domRange.setEnd(range.endContainer!, range.endOffset!);

          const cfi = new EpubCFI(domRange, cfiBase).toString();
          yield cfi;
          counter = 0;
        }
      }
      prev = node;
    }

    // Close remaining range
    if (range.startContainer && prev) {
      range.endContainer = prev;
      range.endOffset = prev.length;

      const domRange = doc.createRange();
      domRange.setStart(range.startContainer, range.startOffset!);
      domRange.setEnd(range.endContainer!, range.endOffset!);

      const cfi = new EpubCFI(domRange, cfiBase).toString();
      yield cfi;
    }
  }

  // ---- Lookup ----

  locationFromCfi(cfi: string): number {
    if (this._locations.length === 0) return -1;
    const loc = locationOf(cfi, this._locations, (a, b) =>
      this._cfi.compare(a, b),
    );
    return Math.min(loc, this._total);
  }

  percentageFromCfi(cfi: string): number {
    if (this._locations.length === 0) return 0;
    const loc = this.locationFromCfi(cfi);
    return this.percentageFromLocation(loc);
  }

  percentageFromLocation(loc: number): number {
    if (!loc || !this._total) return 0;
    return loc / this._total;
  }

  cfiFromLocation(loc: number): string {
    if (typeof loc !== "number") loc = parseInt(String(loc), 10);
    if (loc >= 0 && loc < this._locations.length) {
      return this._locations[loc];
    }
    return "";
  }

  cfiFromPercentage(percentage: number): string {
    if (percentage >= 1) {
      const cfi = new EpubCFI(this._locations[this._total]);
      cfi.collapse();
      return cfi.toString();
    }
    const loc = Math.ceil(this._total * percentage);
    return this.cfiFromLocation(loc);
  }

  // ---- Serialization ----

  load(locations: string | string[]): string[] {
    if (typeof locations === "string") {
      this._locations = JSON.parse(locations);
    } else {
      this._locations = locations;
    }
    this._total = this._locations.length - 1;
    return this._locations;
  }

  save(): string {
    return JSON.stringify(this._locations);
  }

  // ---- Current ----

  get currentLocation(): number {
    return this._current;
  }

  set currentLocation(curr: string | number) {
    if (typeof curr === "string") {
      this._current = this.locationFromCfi(curr);
    } else if (typeof curr === "number") {
      this._current = curr;
    }
  }

  length(): number {
    return this._locations.length;
  }

  get total(): number {
    return this._total;
  }

  destroy(): void {
    this._generation++;
    this._locations = [];
    this._total = 0;
  }
}
